import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Sidebar } from "primereact/sidebar";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";
import { ProgressSpinner } from "primereact/progressspinner";

import { toast } from "react-toastify";

import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbEye,
  TbShieldCheck,
  TbUsers,
  TbSearch,
  TbCheck,
  TbX,
  TbLock,
  TbRefresh,
  TbChevronLeft,
  TbChevronRight,
} from "react-icons/tb";

import api from "../api/axios";
import "../assets/styles/rolesPermissions.css";

const PAGE_NAME = "Roles & Permissions";
const PAGE_SIZE = 10;

const emptyRoleForm = {
  role_name: "",
  description: "",
  status: "active",
};

const emptyPermission = {
  can_view: false,
  can_add: false,
  can_edit: false,
  can_delete: false,
};

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (
  error,
  fallbackMessage
) => {
  /*
   * The global Axios interceptor displays
   * the access-denied dialog for 403 responses.
   */
  if (error.response?.status === 403) {
    return;
  }

  toast.error(
    error.response?.data?.message ||
      fallbackMessage
  );
};

const normaliseBoolean = (value) => {
  return (
    value === true ||
    value === 1 ||
    value === "1"
  );
};

const normalisePermission = (
  permission = {}
) => ({
  page_id: Number(
    permission.page_id ||
      permission.page?.page_id ||
      0
  ),

  can_view: normaliseBoolean(
    permission.can_view
  ),

  can_add: normaliseBoolean(
    permission.can_add
  ),

  can_edit: normaliseBoolean(
    permission.can_edit
  ),

  can_delete: normaliseBoolean(
    permission.can_delete
  ),
});

const getRoleDescription = (role) => {
  return (
    role?.description ||
    role?.role_description ||
    ""
  );
};

const RolesPermissions = () => {
  const [roles, setRoles] = useState([]);

  const [pageMatrix, setPageMatrix] =
    useState({
      menu_groups: [],
      standalone_pages: [],
    });

  const [pagination, setPagination] =
    useState({
      page: 1,
      total: 0,
      totalPages: 1,
    });

  const [search, setSearch] = useState("");
  const [
    debouncedSearch,
    setDebouncedSearch,
  ] = useState("");

  const [loading, setLoading] =
    useState(false);

  const [pageLoading, setPageLoading] =
    useState(false);

  const [roleLoading, setRoleLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [deletingRoleId, setDeletingRoleId] =
    useState(null);

  const [
    formSidebarOpen,
    setFormSidebarOpen,
  ] = useState(false);

  const [
    viewSidebarOpen,
    setViewSidebarOpen,
  ] = useState(false);

  const [
    editingRoleId,
    setEditingRoleId,
  ] = useState(null);

  const [
    selectedRole,
    setSelectedRole,
  ] = useState(null);

  const [roleForm, setRoleForm] = useState(
    emptyRoleForm
  );

  const [permissions, setPermissions] =
    useState({});

  const isEdit = Boolean(editingRoleId);

  const allPages = useMemo(() => {
    const groupedPages = (
      pageMatrix.menu_groups || []
    ).flatMap(
      (group) => group.pages || []
    );

    return [
      ...groupedPages,
      ...(pageMatrix.standalone_pages ||
        []),
    ];
  }, [pageMatrix]);

  const activeRoles = useMemo(() => {
    return roles.filter(
      (role) => role.status === "active"
    ).length;
  }, [roles]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 350);

    return () => clearTimeout(timer);
  }, [search]);

  const createEmptyPermissionMap =
    useCallback(() => {
      const nextPermissions = {};

      allPages.forEach((page) => {
        nextPermissions[
          Number(page.page_id)
        ] = {
          ...emptyPermission,
        };
      });

      return nextPermissions;
    }, [allPages]);

  const mapExistingPermissions =
    useCallback(
      (rolePermissions = []) => {
        const nextPermissions =
          createEmptyPermissionMap();

        rolePermissions.forEach(
          (permission) => {
            const normalised =
              normalisePermission(
                permission
              );

            if (!normalised.page_id) {
              return;
            }

            nextPermissions[
              normalised.page_id
            ] = {
              can_view:
                normalised.can_view,
              can_add: normalised.can_add,
              can_edit:
                normalised.can_edit,
              can_delete:
                normalised.can_delete,
            };
          }
        );

        return nextPermissions;
      },
      [createEmptyPermissionMap]
    );

  const fetchPermissionPages =
    useCallback(async () => {
      try {
        setPageLoading(true);

        const response = await api.get(
          "/admin/access-control/permission-pages",
          {
            headers:
              getPermissionHeaders("view"),
          }
        );

        const responseData =
          response.data?.data || {};

        setPageMatrix({
          menu_groups: Array.isArray(
            responseData.menu_groups
          )
            ? responseData.menu_groups
            : [],

          standalone_pages: Array.isArray(
            responseData.standalone_pages
          )
            ? responseData.standalone_pages
            : [],
        });
      } catch (error) {
        setPageMatrix({
          menu_groups: [],
          standalone_pages: [],
        });

        showRequestError(
          error,
          "Failed to load permission pages."
        );
      } finally {
        setPageLoading(false);
      }
    }, []);

  const fetchRoles = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);

        const response = await api.get(
          "/admin/access-control/roles",
          {
            headers:
              getPermissionHeaders("view"),

            params: {
              page,
              limit: PAGE_SIZE,
              search:
                debouncedSearch ||
                undefined,
            },
          }
        );

        const roleRows = Array.isArray(
          response.data?.data
        )
          ? response.data.data
          : [];

        setRoles(roleRows);

        setPagination(
          response.data?.pagination || {
            page,
            total: roleRows.length,
            totalPages: 1,
          }
        );
      } catch (error) {
        setRoles([]);

        showRequestError(
          error,
          "Failed to load roles."
        );
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearch]
  );

  useEffect(() => {
    fetchPermissionPages();
  }, [fetchPermissionPages]);

  useEffect(() => {
    fetchRoles(1);
  }, [fetchRoles]);

  const resetRoleForm = () => {
    setEditingRoleId(null);
    setSelectedRole(null);
    setRoleForm(emptyRoleForm);
    setPermissions(
      createEmptyPermissionMap()
    );
  };

  const closeFormSidebar = () => {
    setFormSidebarOpen(false);
    resetRoleForm();
  };

  const closeViewSidebar = () => {
    setViewSidebarOpen(false);
    setSelectedRole(null);
  };

  const openCreateRole = () => {
    setEditingRoleId(null);
    setSelectedRole(null);
    setRoleForm(emptyRoleForm);
    setPermissions(
      createEmptyPermissionMap()
    );
    setFormSidebarOpen(true);
  };

  const fetchRoleById = async (
    roleId
  ) => {
    const response = await api.get(
      `/admin/access-control/roles/${roleId}`,
      {
        headers:
          getPermissionHeaders("view"),
      }
    );

    return response.data?.data || null;
  };

  const openEditRole = async (role) => {
    try {
      setRoleLoading(true);

      const roleData =
        (await fetchRoleById(
          role.role_id
        )) || role;

      setEditingRoleId(
        roleData.role_id
      );

      setSelectedRole(roleData);

      setRoleForm({
        role_name:
          roleData.role_name || "",

        description:
          getRoleDescription(roleData),

        status:
          roleData.status || "active",
      });

      setPermissions(
        mapExistingPermissions(
          roleData.permissions || []
        )
      );

      setViewSidebarOpen(false);
      setFormSidebarOpen(true);
    } catch (error) {
      showRequestError(
        error,
        "Failed to load role details."
      );
    } finally {
      setRoleLoading(false);
    }
  };

  const openViewRole = async (role) => {
    try {
      setRoleLoading(true);

      const roleData =
        (await fetchRoleById(
          role.role_id
        )) || role;

      setSelectedRole(roleData);
      setViewSidebarOpen(true);
    } catch (error) {
      showRequestError(
        error,
        "Failed to load role details."
      );
    } finally {
      setRoleLoading(false);
    }
  };

  const updatePermission = (
    pageId,
    permissionName,
    checked
  ) => {
    const numericPageId = Number(pageId);

    setPermissions((previous) => {
      const current =
        previous[numericPageId] || {
          ...emptyPermission,
        };

      const updated = {
        ...current,
        [permissionName]: checked,
      };

      /*
       * Add, edit and delete access are not useful
       * without page-view access.
       */
      if (
        checked &&
        [
          "can_add",
          "can_edit",
          "can_delete",
        ].includes(permissionName)
      ) {
        updated.can_view = true;
      }

      /*
       * Removing view removes all dependent actions.
       */
      if (
        permissionName === "can_view" &&
        !checked
      ) {
        updated.can_add = false;
        updated.can_edit = false;
        updated.can_delete = false;
      }

      return {
        ...previous,
        [numericPageId]: updated,
      };
    });
  };

  const togglePageAllPermissions = (
    pageId,
    checked
  ) => {
    const numericPageId = Number(pageId);

    setPermissions((previous) => ({
      ...previous,

      [numericPageId]: {
        can_view: checked,
        can_add: checked,
        can_edit: checked,
        can_delete: checked,
      },
    }));
  };

  const toggleGroupPermissions = (
    pages,
    checked
  ) => {
    setPermissions((previous) => {
      const next = {
        ...previous,
      };

      (pages || []).forEach((page) => {
        next[Number(page.page_id)] = {
          can_view: checked,
          can_add: checked,
          can_edit: checked,
          can_delete: checked,
        };
      });

      return next;
    });
  };

  const isGroupFullySelected = (
    pages
  ) => {
    if (!pages?.length) {
      return false;
    }

    return pages.every((page) => {
      const permission =
        permissions[
          Number(page.page_id)
        ] || emptyPermission;

      return (
        permission.can_view &&
        permission.can_add &&
        permission.can_edit &&
        permission.can_delete
      );
    });
  };

  const buildPermissionPayload = () => {
    return allPages.map((page) => {
      const pageId = Number(
        page.page_id
      );

      const permission =
        permissions[pageId] ||
        emptyPermission;

      return {
        page_id: pageId,
        can_view: Boolean(
          permission.can_view
        ),
        can_add: Boolean(
          permission.can_add
        ),
        can_edit: Boolean(
          permission.can_edit
        ),
        can_delete: Boolean(
          permission.can_delete
        ),
      };
    });
  };

  const submitRole = async (event) => {
    event.preventDefault();

    const cleanRoleName =
      roleForm.role_name.trim();

    const cleanDescription =
      roleForm.description.trim();

    if (!cleanRoleName) {
      toast.error(
        "Role name is required."
      );
      return;
    }

    if (allPages.length === 0) {
      toast.error(
        "No permission pages are available."
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        role_name: cleanRoleName,
        description: cleanDescription,
        status: roleForm.status,
        permissions:
          buildPermissionPayload(),
      };

      if (isEdit) {
        await api.put(
          `/admin/access-control/roles/${editingRoleId}`,
          payload,
          {
            headers:
              getPermissionHeaders("edit"),
          }
        );

        toast.success(
          "Role updated successfully."
        );
      } else {
        await api.post(
          "/admin/access-control/roles",
          payload,
          {
            headers:
              getPermissionHeaders("add"),
          }
        );

        toast.success(
          "Role created successfully."
        );
      }

      const targetPage = isEdit
        ? pagination.page
        : 1;

      closeFormSidebar();

      await fetchRoles(targetPage);
    } catch (error) {
      showRequestError(
        error,
        "Failed to save role."
      );
    } finally {
      setSaving(false);
    }
  };

  const confirmDeleteRole = (role) => {
    if (role.is_system) {
      toast.error(
        "System roles cannot be deleted."
      );
      return;
    }

    if (
      Number(role.admin_count || 0) > 0
    ) {
      toast.error(
        "Remove all assigned administrators before deleting this role."
      );
      return;
    }

    confirmDialog({
      header: "Delete Role",

      message: `Delete the "${role.role_name}" role? This action cannot be undone.`,

      icon: "pi pi-exclamation-triangle",
      acceptClassName:
        "p-button-danger",
      rejectLabel: "Cancel",
      acceptLabel: "Delete",

      accept: async () => {
        try {
          setDeletingRoleId(
            role.role_id
          );

          await api.delete(
            `/admin/access-control/roles/${role.role_id}`,
            {
              headers:
                getPermissionHeaders(
                  "delete"
                ),
            }
          );

          toast.success(
            "Role deleted successfully."
          );

          const targetPage =
            roles.length === 1 &&
            pagination.page > 1
              ? pagination.page - 1
              : pagination.page;

          await fetchRoles(targetPage);
        } catch (error) {
          showRequestError(
            error,
            "Failed to delete role."
          );
        } finally {
          setDeletingRoleId(null);
        }
      },
    });
  };

  const formatDate = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleDateString(
      "en-IN",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }
    );
  };

  const countEnabledPages = (role) => {
    return (
      role.permissions || []
    ).filter(
      (permission) =>
        normaliseBoolean(
          permission.can_view
        ) ||
        normaliseBoolean(
          permission.can_add
        ) ||
        normaliseBoolean(
          permission.can_edit
        ) ||
        normaliseBoolean(
          permission.can_delete
        )
    ).length;
  };

  const getPermissionCount = (
    role
  ) => {
    if (
      role.permission_count !==
        undefined &&
      role.permission_count !== null
    ) {
      return Number(
        role.permission_count
      );
    }

    return countEnabledPages(role);
  };

  const renderPermissionRow = (
    page
  ) => {
    const pageId = Number(
      page.page_id
    );

    const permission =
      permissions[pageId] ||
      emptyPermission;

    const allChecked =
      permission.can_view &&
      permission.can_add &&
      permission.can_edit &&
      permission.can_delete;

    return (
      <div
        className="permission-matrix-row"
        key={pageId}
      >
        <div className="permission-page-details">
          <div className="permission-page-icon">
            <i
              className={
                page.icon ||
                "pi pi-file"
              }
            />
          </div>

          <div>
            <strong>{page.name}</strong>

            <span>
              {page.path ||
                "Internal page"}
            </span>
          </div>
        </div>

        <label className="permission-checkbox">
          <input
            type="checkbox"
            checked={
              permission.can_view
            }
            onChange={(event) =>
              updatePermission(
                pageId,
                "can_view",
                event.target.checked
              )
            }
          />

          <span>View</span>
        </label>

        <label className="permission-checkbox">
          <input
            type="checkbox"
            checked={
              permission.can_add
            }
            onChange={(event) =>
              updatePermission(
                pageId,
                "can_add",
                event.target.checked
              )
            }
          />

          <span>Add</span>
        </label>

        <label className="permission-checkbox">
          <input
            type="checkbox"
            checked={
              permission.can_edit
            }
            onChange={(event) =>
              updatePermission(
                pageId,
                "can_edit",
                event.target.checked
              )
            }
          />

          <span>Edit</span>
        </label>

        <label className="permission-checkbox">
          <input
            type="checkbox"
            checked={
              permission.can_delete
            }
            onChange={(event) =>
              updatePermission(
                pageId,
                "can_delete",
                event.target.checked
              )
            }
          />

          <span>Delete</span>
        </label>

        <label className="permission-all-toggle">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={(event) =>
              togglePageAllPermissions(
                pageId,
                event.target.checked
              )
            }
          />

          <span>All</span>
        </label>
      </div>
    );
  };

  return (
    <section className="admin-page roles-permissions-page">
      <ConfirmDialog />

      <div className="page-header roles-page-header">
        <div>
          <p>Access Control</p>

          <h1>
            Roles &amp; Permissions
          </h1>

          <span>
            Create administrator roles and
            control page-level access.
          </span>
        </div>

        <div className="roles-page-actions">
          <button
            type="button"
            className="ghost-action bottonborder"
            onClick={() => {
              fetchPermissionPages();
              fetchRoles(
                pagination.page
              );
            }}
            disabled={
              loading || pageLoading
            }
          >
            <TbRefresh />
            Refresh
          </button>

          <button
            type="button"
            className="primary-action"
            onClick={openCreateRole}
            disabled={pageLoading}
          >
            <TbPlus />
            Add Role
          </button>
        </div>
      </div>

      <div className="page-card roles-list-card">
        <div className="roles-toolbar">
          <div>
            <h2>
              Administrator Roles
            </h2>

            <p>
              {loading
                ? "Loading roles..."
                : ``}
            </p>
          </div>

          <div className="roles-search">
            <TbSearch />

            <input
              value={search}
              placeholder="Search roles..."
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
            />

            {search && (
              <button
                type="button"
                className="roles-search-clear"
                onClick={() =>
                  setSearch("")
                }
                aria-label="Clear search"
              >
                <TbX />
              </button>
            )}
          </div>
        </div>

        {loading ? (
          <div className="roles-loading-state">
            <ProgressSpinner
              style={{
                width: "42px",
                height: "42px",
              }}
              strokeWidth="4"
            />

            <p>Loading roles...</p>
          </div>
        ) : (
          <div className="roles-grid">
            {roles.length === 0 && (
              <div className="role-empty-state">
                <TbShieldCheck />

                <h3>No roles found</h3>

                <p>
                  Add a role or change your
                  search query.
                </p>
              </div>
            )}

            {roles.map((role) => {
              const assignedAdminCount =
                Number(
                  role.admin_count || 0
                );

              const cannotDelete =
                Boolean(role.is_system) ||
                assignedAdminCount > 0 ||
                Number(
                  deletingRoleId
                ) ===
                  Number(role.role_id);

              return (
                <article
                  className={`role-card ${
                    role.is_system
                      ? "system-role-card"
                      : ""
                  }`}
                  key={role.role_id}
                >
                  <div className="role-card-icon">
                    {role.is_system ? (
                      <TbLock />
                    ) : (
                      <TbShieldCheck />
                    )}
                  </div>

                  <div className="role-card-content">
                    <div className="role-card-title">
                      <div>
                        <div className="role-name-line">
                          <h3>
                            {role.role_name}
                          </h3>

                          {role.is_system && (
                            <span className="system-role-badge">
                              System
                            </span>
                          )}
                        </div>

                        <p>
                          {getRoleDescription(
                            role
                          ) ||
                            "No description provided."}
                        </p>
                      </div>

                      <code>
                        #{role.role_id}
                      </code>
                    </div>

                    <div className="role-status-row">
                      <span
                        className={`status-badge ${
                          role.status ||
                          "active"
                        }`}
                      >
                        {role.status ||
                          "active"}
                      </span>
                    </div>

                    <div className="role-stat-grid">
                      <div>
                        <TbUsers />

                        <span>
                          <strong>
                            {
                              assignedAdminCount
                            }
                          </strong>

                          Assigned admins
                        </span>
                      </div>

                      <div>
                        <TbShieldCheck />

                        <span>
                          <strong>
                            {getPermissionCount(
                              role
                            )}
                          </strong>

                          Permission pages
                        </span>
                      </div>
                    </div>

                    <div className="role-card-footer">
                      <small>
                        Updated{" "}
                        {formatDate(
                          role.updatedAt
                        )}
                      </small>

                      <div>
                        <button
                          type="button"
                          className="ghost-action"
                          onClick={() =>
                            openViewRole(
                              role
                            )
                          }
                          aria-label={`View ${role.role_name}`}
                        >
                          <TbEye />
                        </button>

                        <button
                          type="button"
                          className="ghost-action"
                          onClick={() =>
                            openEditRole(
                              role
                            )
                          }
                          aria-label={`Edit ${role.role_name}`}
                        >
                          <TbEdit />
                        </button>

                        <button
                          type="button"
                          className="ghost-action danger-action"
                          disabled={
                            cannotDelete
                          }
                          onClick={() =>
                            confirmDeleteRole(
                              role
                            )
                          }
                          title={
                            role.is_system
                              ? "System roles cannot be deleted"
                              : assignedAdminCount >
                                  0
                                ? "This role is assigned to administrators"
                                : "Delete role"
                          }
                          aria-label={`Delete ${role.role_name}`}
                        >
                          <TbTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="pagination-bar">
          <button
            type="button"
            className="ghost-action"
            disabled={
              pagination.page <= 1 ||
              loading
            }
            onClick={() =>
              fetchRoles(
                pagination.page - 1
              )
            }
          >
            <TbChevronLeft />
            Previous
          </button>

          <span>
            Page {pagination.page} of{" "}
            {Math.max(
              pagination.totalPages,
              1
            )}
          </span>

          <button
            type="button"
            className="ghost-action"
            disabled={
              pagination.page >=
                pagination.totalPages ||
              loading
            }
            onClick={() =>
              fetchRoles(
                pagination.page + 1
              )
            }
          >
            Next
            <TbChevronRight />
          </button>
        </div>
      </div>

      {/* CREATE / EDIT ROLE */}

      <Sidebar
        visible={formSidebarOpen}
        position="right"
        onHide={closeFormSidebar}
        className="role-form-sidebar"
        dismissable={!saving}
        closeOnEscape={!saving}
      >
        <form
          className="role-form-shell"
          onSubmit={submitRole}
        >
          <div className="role-form-header">
            <div className="role-form-header-icon">
              {isEdit ? (
                <TbEdit />
              ) : (
                <TbPlus />
              )}
            </div>

            <div>
              <p>Access Control</p>

              <h2>
                {isEdit
                  ? "Edit Role"
                  : "Create Role"}
              </h2>

              <span>
                Configure role details and
                page-level access.
              </span>
            </div>
          </div>

          <div className="role-basic-fields">
            <label>
              Role Name

              <input
                value={
                  roleForm.role_name
                }
                maxLength={100}
                required
                placeholder="Example: Product Manager"
                disabled={
                  Boolean(
                    selectedRole?.is_system
                  )
                }
                onChange={(event) =>
                  setRoleForm(
                    (previous) => ({
                      ...previous,
                      role_name:
                        event.target
                          .value,
                    })
                  )
                }
              />

              {selectedRole?.is_system && (
                <small>
                  The name of a system role
                  cannot be changed.
                </small>
              )}
            </label>

            <label>
              Status

              <select
                value={roleForm.status}
                disabled={
                  Boolean(
                    selectedRole?.is_system
                  )
                }
                onChange={(event) =>
                  setRoleForm(
                    (previous) => ({
                      ...previous,
                      status:
                        event.target
                          .value,
                    })
                  )
                }
              >
                <option value="active">
                  Active
                </option>

                <option value="inactive">
                  Inactive
                </option>
              </select>
            </label>

            <label className="role-description-field">
              Description

              <textarea
                rows={4}
                maxLength={255}
                value={
                  roleForm.description
                }
                placeholder="Describe this administrator role"
                onChange={(event) =>
                  setRoleForm(
                    (previous) => ({
                      ...previous,
                      description:
                        event.target
                          .value,
                    })
                  )
                }
              />

              <small>
                {
                  roleForm.description
                    .length
                }
                /255 characters
              </small>
            </label>
          </div>

          <div className="permission-matrix">
            <div className="permission-matrix-header">
              <div>
                <h3>
                  Page Permissions
                </h3>

                <p>
                  Add, edit and delete
                  permissions automatically
                  enable view permission.
                </p>
              </div>

              <div className="permission-matrix-actions">
                <button
                  type="button"
                  className="permission-group-action"
                  disabled={
                    allPages.length === 0
                  }
                  onClick={() =>
                    toggleGroupPermissions(
                      allPages,
                      true
                    )
                  }
                >
                  <TbCheck />
                  Select everything
                </button>

                <button
                  type="button"
                  className="permission-group-action"
                  disabled={
                    allPages.length === 0
                  }
                  onClick={() =>
                    toggleGroupPermissions(
                      allPages,
                      false
                    )
                  }
                >
                  <TbX />
                  Clear everything
                </button>
              </div>
            </div>

            {pageLoading ? (
              <div className="permission-loading">
                <ProgressSpinner
                  style={{
                    width: "38px",
                    height: "38px",
                  }}
                  strokeWidth="4"
                />

                <span>
                  Loading permission pages...
                </span>
              </div>
            ) : allPages.length === 0 ? (
              <div className="permission-loading">
                <TbShieldCheck />

                <span>
                  No permission pages are
                  available.
                </span>
              </div>
            ) : (
              <>
                {(
                  pageMatrix.menu_groups ||
                  []
                ).map((group) => {
                  const groupPages =
                    group.pages || [];

                  const allSelected =
                    isGroupFullySelected(
                      groupPages
                    );

                  return (
                    <section
                      className="permission-group"
                      key={
                        group.menu_group_id
                      }
                    >
                      <div className="permission-group-header">
                        <div className="permission-group-title">
                          <div className="permission-group-icon">
                            <i
                              className={
                                group.icon ||
                                "pi pi-folder"
                              }
                            />
                          </div>

                          <div>
                            <h4>
                              {group.name}
                            </h4>

                            <span>
                              {
                                groupPages.length
                              }{" "}
                              page(s)
                            </span>
                          </div>
                        </div>

                        <div>
                          <button
                            type="button"
                            className="permission-group-action"
                            onClick={() =>
                              toggleGroupPermissions(
                                groupPages,
                                !allSelected
                              )
                            }
                          >
                            {allSelected ? (
                              <TbX />
                            ) : (
                              <TbCheck />
                            )}

                            {allSelected
                              ? "Clear group"
                              : "Select group"}
                          </button>
                        </div>
                      </div>

                      {groupPages.map(
                        renderPermissionRow
                      )}
                    </section>
                  );
                })}

                {(
                  pageMatrix.standalone_pages ||
                  []
                ).length > 0 && (
                  <section className="permission-group">
                    <div className="permission-group-header">
                      <div className="permission-group-title">
                        <div className="permission-group-icon">
                          <i className="pi pi-file" />
                        </div>

                        <div>
                          <h4>
                            Standalone Pages
                          </h4>

                          <span>
                            {
                              pageMatrix
                                .standalone_pages
                                .length
                            }{" "}
                            page(s)
                          </span>
                        </div>
                      </div>

                      <div>
                        <button
                          type="button"
                          className="permission-group-action"
                          onClick={() =>
                            toggleGroupPermissions(
                              pageMatrix.standalone_pages,
                              !isGroupFullySelected(
                                pageMatrix.standalone_pages
                              )
                            )
                          }
                        >
                          {isGroupFullySelected(
                            pageMatrix.standalone_pages
                          ) ? (
                            <TbX />
                          ) : (
                            <TbCheck />
                          )}

                          {isGroupFullySelected(
                            pageMatrix.standalone_pages
                          )
                            ? "Clear group"
                            : "Select group"}
                        </button>
                      </div>
                    </div>

                    {pageMatrix.standalone_pages.map(
                      renderPermissionRow
                    )}
                  </section>
                )}
              </>
            )}
          </div>

          <div className="role-form-footer">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={closeFormSidebar}
              disabled={saving}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="primary-action"
              disabled={
                saving ||
                pageLoading ||
                allPages.length === 0
              }
            >
              {saving
                ? "Saving..."
                : isEdit
                  ? "Update Role"
                  : "Create Role"}
            </button>
          </div>
        </form>
      </Sidebar>

      {/* VIEW ROLE */}

      <Sidebar
        visible={viewSidebarOpen}
        position="right"
        onHide={closeViewSidebar}
        className="role-view-sidebar"
      >
        {roleLoading ? (
          <div className="role-view-loading">
            <ProgressSpinner
              style={{
                width: "42px",
                height: "42px",
              }}
              strokeWidth="4"
            />

            <p>Loading role...</p>
          </div>
        ) : (
          selectedRole && (
            <div className="role-view-shell">
              <div className="role-view-icon">
                {selectedRole.is_system ? (
                  <TbLock />
                ) : (
                  <TbShieldCheck />
                )}
              </div>

              <div className="role-view-title">
                <p>
                  Administrator Role
                </p>

                <div className="role-name-line">
                  <h2>
                    {
                      selectedRole.role_name
                    }
                  </h2>

                  {selectedRole.is_system && (
                    <span className="system-role-badge">
                      System
                    </span>
                  )}
                </div>

                <span>
                  {getRoleDescription(
                    selectedRole
                  ) ||
                    "No description provided."}
                </span>
              </div>

              <div className="role-view-status">
                <span
                  className={`status-badge ${
                    selectedRole.status ||
                    "active"
                  }`}
                >
                  {selectedRole.status ||
                    "active"}
                </span>

                <code>
                  Role #
                  {selectedRole.role_id}
                </code>
              </div>

              <div className="role-view-stats">
                <div>
                  <TbUsers />

                  <strong>
                    {Number(
                      selectedRole.admin_count ||
                        0
                    )}
                  </strong>

                  <span>
                    Assigned admins
                  </span>
                </div>

                <div>
                  <TbShieldCheck />

                  <strong>
                    {getPermissionCount(
                      selectedRole
                    )}
                  </strong>

                  <span>
                    Permission pages
                  </span>
                </div>
              </div>

              <div className="role-view-permissions">
                <div className="role-view-section-title">
                  <div>
                    <h3>Permissions</h3>

                    <p>
                      Access currently assigned
                      to this role.
                    </p>
                  </div>
                </div>

                {(
                  selectedRole.permissions ||
                  []
                ).length === 0 ? (
                  <p className="role-no-permissions">
                    No permissions assigned.
                  </p>
                ) : (
                  (
                    selectedRole.permissions ||
                    []
                  ).map(
                    (
                      permission,
                      index
                    ) => {
                      const enabledActions = [
                        permission.can_view &&
                          "View",
                        permission.can_add &&
                          "Add",
                        permission.can_edit &&
                          "Edit",
                        permission.can_delete &&
                          "Delete",
                      ].filter(Boolean);

                      return (
                        <div
                          className="role-view-permission-row"
                          key={
                            permission.permission_id ||
                            `${permission.page_id}-${index}`
                          }
                        >
                          <div>
                            <strong>
                              {permission.page
                                ?.name ||
                                `Page ${permission.page_id}`}
                            </strong>

                            <span>
                              {permission.page
                                ?.path ||
                                "Internal page"}
                            </span>
                          </div>

                          <div>
                            {enabledActions.length >
                            0 ? (
                              enabledActions.map(
                                (action) => (
                                  <span
                                    key={
                                      action
                                    }
                                  >
                                    {action}
                                  </span>
                                )
                              )
                            ) : (
                              <span className="permission-none-chip">
                                No access
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    }
                  )
                )}
              </div>

              <div className="role-view-footer">
                <button
                  type="button"
                  className="ghost-action bottonborder"
                  onClick={closeViewSidebar}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="primary-action"
                  onClick={() =>
                    openEditRole(
                      selectedRole
                    )
                  }
                >
                  <TbEdit />
                  Edit Role
                </button>
              </div>
            </div>
          )
        )}
      </Sidebar>
    </section>
  );
};

export default RolesPermissions;